<template>
  <v-container fluid class="pt-0">
    <v-row>
      <v-col cols="12" class="px-0 pt-0">
        <v-data-table
          :headers="headers"
          :items="items"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          hide-default-footer
          dense
          class="elevation-1 pt-2 body-1"
          item-key="id"
          @page-count="pageCount = $event"
          :key="notesTableKey"
          sort-by="updatedAt" :sort-desc="true"
        >
          <!--Notes-->
          <template v-slot:item.notes="{ item }" >
            <span class="body-1 primary--text">{{ item.notes }}</span>
          </template>
          <!--Type-->
          <template v-slot:item.typeId="{ item }" >
            <span><v-icon class="me-2" small>{{ getDocumentIconById(item.typeId) }}</v-icon>{{ getDocumentType(item) }}</span>
          </template>
          <!--Overall Score-->
          <template v-slot:item.overallScore="{ item }" >
            <div class="ma-0 py-1">
              <v-chip x-small :color="getOverallScoreColor(item.overallScore)" class="monospace-font">{{ item.overallScore }}</v-chip>
            </div>
          </template>
          <!--TS Updated-->
          <template v-slot:item.updatedAt="{ item }" >
            <span v-if="item.updatedAt">{{ formatTimestamp(item.updatedAt) }}</span>
            <span v-else-if="item.createdAt">{{ formatTimestamp(item.createdAt) }}</span>
            <span v-else>-</span>
          </template>
          <!--Actions-->
          <template v-slot:item.actions="{ item }" >
            <AddQuickNote
              v-if="item.createdBy === currentUser.username"
              :type="type"
              :documentTypes="isDallas ? dallasNoteTypes : documentTypes"
              buttonType="editButton"
              :isEditing="true"
              :noteToUpdate="item"
              @dialogClosed="handleDialogClosed"
              @postSearch="handlePostSearch($event)"
            ></AddQuickNote>
            <v-btn v-if="item.createdBy === currentUser.username" elevation="0" icon x-small class="ms-1" color="grey lighten-1" @click="onDeleteClick(item)">
              <v-icon small>delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
        <div v-if="pageCount > 1" class="text-center pt-2">
          <v-pagination class="monospace-font" color="secondary" circle v-model="page" :length="pageCount"></v-pagination>
        </div>
      </v-col>
    </v-row>
    <!--Delete analysis confirmation-->
    <ConfirmationModal
      @confirmation="onRemoveNoteConfirmation($event)"
      ref="confirmationRemoveModal"
      title="documents.deleteQuickNote"
      message="documents.deleteQuickNoteMessage"
    ></ConfirmationModal>
  </v-container>
</template>

<script>

import * as _ from "lodash";
import * as moment from 'moment';
import ConfirmationModal from "../../base/ConfirmationModal.vue";
import AddQuickNote from './AddQuickNote.vue';
import DocumentService from "../../../services/document.service";
import { getDocumentIconById, colorScale } from "../../../utils/functions";

export default {
  name: "QuickNotesTable",
  props: {
    items: {
      type: Array,
      default: () => []
    },
    documentTypes: {
      type: Array,
      default: () => []
    },
    type: {
      type: String,
      default: 'player'
    }
  },
  components: { ConfirmationModal, AddQuickNote },
  data() {
    return {
      noteToDelete: null,
      failed_image: false,
      itemsPerPage: 7,
      page: 1,
      pageCount: 0,
      notesTableKey: 0,
      overallRatingItems: ['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'C1', 'C2', 'C3', 'D1', 'D2', 'D3', 'E1', 'E2', 'E3'],
      headers: [
        {
          text: this.$t('general.notes'),
          align: "start",
          value: "notes",
          sortable: false,
          width: "50%"
        },
        {
          text: this.$t('blog.author'),
          align: "start",
          value: "createdBy",
          sortable: false,
        },
        {
          text: this.$t('scoutingAnalysis.lastEdited'),
          align: "start",
          value: "updatedAt",
          sortable: false,
        },
        {
          text: "",
          align: "center",
          value: "actions",
          sortable: false,
          width: 80
        },
      ],
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    isDallas() {
      return this.currentUser.clientId === 10400;
    },
    dallasNoteTypes() {
      return [{id: 7, code: 'QUICK_NOTES'}, {id: 9, code: 'GENERAL_COMMENTS'}];
    }
  },
  created() {
    if (this.isDallas) this.headers.unshift(
      { text: this.$t('general.type'), align: "start", value: "typeId", sortable: false, width: 160 },
      { text: this.$t('documents.rating'), align: "start", value: "overallScore", sortable: false });
  },
  methods: {
    formatTimestamp(date) {
      const formattedDate = moment(date, "DD/MM/YYYY HH:mm").format("DD/MM/YYYY");
      return formattedDate;
    },
    onDeleteClick(note) {
      this.noteToDelete = note;
      this.$refs.confirmationRemoveModal.open();
    },
    async onRemoveNoteConfirmation(confirmation){
      if (confirmation) {
        if (this.type === 'player') await DocumentService.deleteDocument(this.noteToDelete);
        else if (this.type === 'team') await DocumentService.deleteTeamDocument(this.noteToDelete);
        else if (this.type === 'game') await DocumentService.deleteGameDocument(this.noteToDelete);
        const postSearchEvent = { operation: "delete", filename: "", documentType: "QUICK_NOTES" };
        this.handlePostSearch(postSearchEvent);
        this.handleDialogClosed();
      };
      this.noteToDelete = null;
      this.$refs.confirmationRemoveModal.close();
    },
    handleDialogClosed() {
      this.notesTableKey++;
      this.$emit('noteChanged');
    },
    handlePostSearch(event) {
      this.$emit("postSearch", event);
    },
    getDocumentIconById(id) {
      return getDocumentIconById(id);
    },
    getDocumentType(document) {
      const type = this.documentTypes.filter(d => d.id === document.typeId)[0];
      return this.$t(`documents.types.${type.code}`);
    },
    getOverallScoreColor(value) {
      const i = this.overallRatingItems.toReversed().indexOf(value);
      if (i > -1) return colorScale(1, 15, i + 1, 100);
    },
  }
};
</script>

<style scoped>
  .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
    padding-top: 0.8em;
    padding-bottom: 0.8em;
  }
</style>



// WEBPACK FOOTER //
// src/views/scouting-analysis/quick-notes/QuickNotesTable.vue